import React, { useEffect, useState } from 'react';
import { Crown, Heart, Eye, Infinity as InfinityIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import api from '../services/api';
import { NOT_ON_SALE_TEXT, BILLING_TERMS_TEXT } from './PublicPages';

const PERKS = [
  { icon: InfinityIcon, text: 'Unlimited likes — no daily cap' },
  { icon: Eye, text: 'See everyone who already liked you' },
  { icon: Heart, text: 'Keep swiping while your matches roll in' },
];

const fmtReset = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'shortly';
  const h = Math.floor(ms / 3600000), m = Math.ceil((ms % 3600000) / 60000);
  return h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
};

// Upsell shown when a free member hits the server-enforced daily like limit (402).
export const UpgradeSheet = ({ open, onOpenChange, allowance, onUpgrade }) => {
  const [offers, setOffers] = useState(null);
  useEffect(() => { if (open && !offers) api.get('/payments/offers').then((r) => setOffers(r.data.data)).catch(() => setOffers({ offers: [], mode: 'test' })); }, [open, offers]);
  const notOnSale = !offers || offers.mode !== 'live';
  const reset = fmtReset(allowance?.resets_at);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upgrade-sheet">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl"><Crown className="w-6 h-6 text-amber-500" />You've used today's {allowance?.limit ?? 25} free likes</DialogTitle>
          <DialogDescription data-testid="upgrade-reset-text">
            {reset ? `Your free likes refresh ${reset}.` : 'Your free likes refresh every 24 hours.'} Go Premium for unlimited likes.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 text-sm" data-testid="upgrade-perks">
          {PERKS.map(({ icon: Icon, text }) => <li key={text} className="flex items-center gap-2"><Icon className="w-4 h-4 text-rose-500" />{text}</li>)}
        </ul>
        <div className="grid gap-2 sm:grid-cols-3" data-testid="upgrade-plans">
          {(offers?.offers || []).map((o) => (
            <div key={o.lookup_key} className="rounded-lg border p-3 text-center" data-testid={`upgrade-plan-${o.lookup_key}`}>
              <p className="text-xs font-semibold text-gray-600">{o.name}</p>
              <p className="text-lg font-bold">${o.amount.toFixed(2)}</p>
              <p className="text-[11px] text-gray-500">${o.per_month.toFixed(2)}/mo{o.savings_pct > 0 ? ` · save ${o.savings_pct}%` : ''}</p>
            </div>
          ))}
        </div>
        {notOnSale && <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="upgrade-not-available">{NOT_ON_SALE_TEXT}</p>}
        <p className="text-[11px] text-gray-500">{BILLING_TERMS_TEXT}</p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="upgrade-later-btn">Maybe later</Button>
          <Button onClick={onUpgrade} className="bg-gradient-to-r from-amber-500 to-rose-500 text-white" data-testid="upgrade-go-btn">Upgrade in Settings</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// "X likes left today" — hidden for Premium (unlimited) members.
export const LikesLeftChip = ({ allowance }) => {
  if (!allowance || allowance.unlimited) return null;
  const low = allowance.remaining <= 5;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${low ? 'bg-rose-100 text-rose-700' : 'bg-white/80 text-gray-700 shadow-sm'}`} data-testid="likes-left-chip">
      <Heart className="w-3 h-3" />{allowance.remaining} of {allowance.limit} likes left today
    </span>
  );
};
